import { accountObj, agencyObj } from '../store'

import {
  removeProtocol,
  createQueryString,
  cloudinaryOptions,
  swapProtocol,
} from '../utils/helpers.js'

const proto = location.protocol + '//'

export function makePublishDomain(app, domains) {
  const { uuid } = accountObj()
  let domain

  if (!domains) {
    if (!app.layoutBuilder) {
      domains = agencyObj().settings.publishDomain
    } else {
      domains = `${removeProtocol(pagesUrl)}/${uuid}`
    }
  }

  if (!Array.isArray(domains)) {
    domains = [domains]
  }

  if (domains.length === 0 && !app.publishDomain) {
    domain = appsUrl.replace(/^.*?:\/\//g, '')
  } else if (domains.length === 1 && !app.publishDomain) {
    domain = domains[0]
  } else {
    domain = app.publishDomain
  }

  return domain
}

/**
 * Generate Microsite Url
 * Returns the microsite URL for the app.
 * @param  {Object} app
 * @param  {String} slug
 * @return {String}
 */
export function generateMicrositeUrl(publishTo, app, slug) {
  return `${proto}${publishTo}/${slug}/${app.slug}`
}

export function makePublishedPreview(publishTo, app, slug) {
  const link = `${proto}${publishTo}/${slug}/${app.slug}`
  return `${link}?ak_track=false&ak_test=true`
}

export function makeDraftPreview(publishTo, app, slug) {
  let link = ''

  if (app.layoutBuilder) {
    link += `${proto}${publishTo}/${slug}/${app.slug}/preview`
  } else {
    link += `${proto}${publishTo}/preview/${slug}/${app.slug}`
  }

  return link
}

/**
 * @param {String} publishTo
 * @param {Object} app
 * @param {String} slug
 * @param {String} type
 * @return {String}
 * @description Generate preview Url
 * Returns the preview URL for the app.
 */
export function generatePreviewUrl(publishTo, app, slug, type) {
  return type === 'draft'
    ? makeDraftPreview.apply(this, [removeProtocol(publishTo), app, slug, type])
    : makePublishedPreview.apply(this, [
        removeProtocol(publishTo),
        app,
        slug,
        type,
      ])
}

/**
 * Fetch Share Urls
 * Returns the share urls tied to the app.
 * @param  {Object} app
 * @param  {String} slug
 * @return {Object}
 */
export function fetchShareUrls(app, slug) {
  const shareUrls = {
    list: [],
    link: '',
  }

  if (!app || !app.publishedStatus) {
    return shareUrls
  }

  // Add microsite share link
  if (app.publishedMicro) {
    if (!slug) {
      return shareUrls
    }

    // Add to share list
    shareUrls.link = app.micrositeShortUrl
    shareUrls.longUrl = generateMicrositeUrl(app, slug)
    shareUrls.list.push({
      value: shareUrls.link,
      longUrl: shareUrls.longUrl,
      label: 'Microsite',
    })
  }

  // Add facebook share links
  // Leaving this here incase anything breaks, im not writing tests for it
  // we don't publish to facebook anymore

  /* istanbul ignore next */
  if (app.publishedFb) {
    for (var i = 0, j = app.publishedTo.length; i < j; i += 1) {
      shareUrls.list.push({
        value: app.publishedTo[i].shortUrl,
        label: app.publishedTo[i].name,
      })
    }
  }

  return shareUrls
}

/**
 * @param {Object} app
 * @returns {Object}
 * @description Returns an app embed object that contains embed codes,
 * links and preview links ect.
 */
export function getAppDetails(app) {
  if (typeof app.account === 'undefined') return {}
  const domain = `${proto}${makePublishDomain(app)}`
  const ts = Date.now()
  const frame = `${domain}/${app.account.slug}/${app.slug}?type=embed&ak_ts=${ts}`
  return {
    title: app.title,
    slug: app.slug,
    accSlug: app.account.slug,
    type: app.type,
    frame,
    domain,
    publishedStatus: app.publishedStatus,
    layoutBuilder: app.layoutBuilder || false,
    resourceDomain:
      app.layoutBuilder === true
        ? domain.substring(0, domain.lastIndexOf('/'))
        : domain,
  }
}

/**
 * @param {Object} app
 * @returns {Object}
 * @description Returns the appType of the given app object
 */
export function appType(app) {
  return getAppDetails(app).type
}

/**
 * @param {Object} app
 * @returns {Boolean}
 * @description Returns the layoutBuilder flag of the given app object
 */
export function isLayout(app) {
  return getAppDetails(app).layoutBuilder
}

/**
 * @param {Object} app
 * @returns {Boolean}
 * @description Returns the iframe url of the given app object
 */
export function frameUrl(app) {
  return getAppDetails(app).frame
}

/**
 *
 * @param {Object} app
 * @param {Boolean} cn
 * @param {Boolean} type
 * @returns {String}
 * @description Returns the embed code for an application
 */
export function code(app, cn, type) {
  const appData = getAppDetails(app)
  const isCn = cn ? '&ak_cn=true' : ''
  const t = type ? type : 'page'

  return /* html */ `
    <!DOCTYPE html>
      <html>
        <head>
          <title>${appData.title}</title>
          <meta http-equiv="X-UA-Compatible" content="IE=edge" />
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <link href="${appsUrl}/css/page-embed.css" rel="stylesheet" />
        </head>
        <body>
          <iframe 
            title="${appData.title}" 
            class="ak-app-target" 
            data-src="${appData.frame}&ak_app=${t}${isCn}" 
            frameborder="0" 
            width="100%" 
            height="100%" 
            scrolling="no" 
          />
          <script src="${appsUrl}/public/contents/js/iframe-embed-v3.js"></script>
        </body>
      </html>
    `
}

/**
 *  Returns the embed code the creating an iframe within
 *  an existing page
 *  @param  {Boolean} cn
 *  @param  {String}  type
 *  @return {String}
 */
export function embedCode(app, cn, type) {
  const appData = getAppDetails(app)
  const isCn = cn ? '&ak_cn=true' : ''
  const t = type ? type : 'page'

  if (app.type === 'embed') {
    return (
      `<iframe title="${appData.title}" class="ak-app-target" data-src="${appData.frame}&ak_app=${t}${isCn}"` +
      ' frameborder="0" width="100%" height="500" scrolling="no"></iframe>\n' +
      `<script src="${appData.domain}/public/contents/js/iframe-embed-v3.js"></script>`
    )
  }

  return (
    `<iframe title="${appData.title}" class="ak-app-target" data-src="${appData.frame}&ak_app=${t}${isCn}"` +
    ' frameborder="0" width="100%" height="1000" scrolling="no"></iframe>\n' +
    `<script src="${appsUrl}/public/contents/js/iframe-embed-v3.js"></script>`
  )
}

/**
 *  returns the link for the embed help documents
 *  @return {String}
 */
export function makeInstallLink(app) {
  const appData = getAppDetails(app)
  const type = appData.type === 'embed' ? 'form-embed' : 'page-embed'
  return `/install/${type}${createQueryString({
    acc: appData.accSlug,
    title: appData.title,
    app: appData.slug,
    domain: appData.domain,
    resourceDomain: appData.resourceDomain,
  })}`
}

/**
 * @param {Object} app
 * @returns {String}
 * @description Returns the preview link for the app
 */
export function previewLink(app) {
  try {
    const appData = getAppDetails(app)
    return generatePreviewUrl(
      appData.domain,
      appData,
      appData.accSlug,
      appData.publishedStatus ? 'live' : 'draft',
    )
  } catch (e) {
    return ''
  }
}

/**
 * @param {Object}
 * @returns {String}
 * @description return the published link with no parameters
 */
export function publishedLink(app) {
  try {
    const appData = getAppDetails(app)
    const url = appData.frame.split('?')[0]
    return url
  } catch (e) {
    return ''
  }
}

export function responsive() {
  return '<meta name="viewport" content="width=device-width,initial-scale=1" />'
}

/**
 * @param {Object} app - The app object to get the first image for
 * @param {Object} options - the options for the image we want
 * @returns
 * @description Tries to get the first image for the given app object
 */
export function getFirstImage(app, options) {
  var url = ''
  var type = 'upload'

  // TODO: Jamie wrote this, so its not very pretty, could do with tidying up

  // First we need to know if the app has a media object, it should
  // but just incase....
  if (app.draft && typeof app.draft.media === 'object') {
    // Because each app layout uses a different 'banner' we need to check each
    // layout type before we go further
    if (app.layout === 'media_centric') {
      // Check if its a video or not first
      if (app.draft.media.bannerNarrow.media === 'video') {
        url = app.draft.media.bannerNarrow.preview
        type = app.draft.media.bannerNarrow.type
      } else {
        // Do we have any images? if so, grab the first one
        /* istanbul ignore else */
        if (app.draft.media.bannerNarrow.items.length > 0) {
          url = app.draft.media.bannerNarrow.items[0].src
        }
      }
      return cloudinaryOptions(url, options, type)
    } else if (
      app.layout === 'form_overlay_with_testimonials' ||
      app.layout === 'popup_form_overlay'
    ) {
      if (app.draft.media.bannerSquare.media === 'video') {
        url = app.draft.media.bannerSquare.preview
        type = app.draft.media.bannerSquare.type
      } else {
        /* istanbul ignore else */
        if (app.draft.media.bannerSquare.items.length > 0) {
          url = app.draft.media.bannerSquare.items[0].src
        }
      }
      return cloudinaryOptions(url, options, type)
    }
    // Used by: classic, form_overlay, classic_double_media

    // Next up is the banner image
    // Check if the banner is a video
    if (app.draft.media.banner.media === 'video') {
      url = app.draft.media.banner.preview
      type = app.draft.media.banner.type
    } else {
      /* istanbul ignore else */
      if (app.draft.media.banner.items.length > 0) {
        url = app.draft.media.banner.items[0].src
      }
    }
    return cloudinaryOptions(url, options, type)
  }
}

/**
 * @param {Object} app - The app to get the preview image for
 * @param {Boolean} thumb -
 * @returns {Boolean|String}
 * @description Returns false if no image was found, else returns
 * the preview image for the given app object
 */
export function getPreviewImage(app, thumb) {
  const config = {
    c: 'fill',
    w: 592,
    h: 450,
  }

  if (thumb === true) {
    config.w = 200
    config.h = 150
  }

  if (!app.layoutBuilder) {
    return swapProtocol(getFirstImage(app, config))
  }

  if (app.screenshotConfig && app.screenshotConfig.url) {
    return `${window.location.origin}/screenshots/${app.screenshotConfig.url}`
  }

  return false
}

/**
 * @param {Object} appService - Angular AppService
 * @returns {Object}
 * @description Allthough this can be used in the Vue code this is meant
 * to be used in the angular codebase to create the same API on the
 * EmbedCode service that used to be there.
 *
 * Used in the 'Apps.Editor.Controllers.EmbedCodes' angular module
 *
 * TODO: replace the `appService` with something in the vue code
 */
export function appEmbedService(appService) {
  const getData = () => appService.getData()
  return {
    getData,
    appType: () => appType(getData()),
    isLayout: () => isLayout(getData()),
    frameUrl: () => frameUrl(getData()),
    code: (cn, type) => code(getData(), cn, type),
    frame: (cn, type) => embedCode(getData(), cn, type),
    link: () => makeInstallLink(getData()),
    preview: () => previewLink(getData()),
    published: () => publishedLink(getData()),
    responsive,
  }
}
